import React, { useState, useEffect } from "react";
import PaginatedTable from "@/components/common/table/PaginatedTable";
import { Offer } from "@/types/offer";
import { useSessionsTable, BulkEditFormData } from "./useSessionsTable";
import { TableHeader } from "./TableHeader";
import { createColumns, type CreateColumnsOptions } from "./columns";
import { DeleteConfirmationDialog } from "./DeleteConfirmationDialog";
import { BulkActionsMenu } from "./BulkActionsMenu";
import { BulkEditModal } from "./BulkEditModal";
import { SortableOption } from "@/types/fieldsOptions";

interface SessionsTableProps {
  offers: Offer[];
  isLoading: boolean;
  onEditOffer: (offer: Offer) => void;
  onDeleteOffer: (offerId: string) => void;
  onCopyOffer?: (offerId: string) => void;
  onBulkUpdateOffers?: (offerIds: string[], updateData: Partial<Offer>, suppressToasts?: boolean) => Promise<void>;
  onDateFilterChange?: (value: 'future' | 'past' | 'all' | 'placeholder') => void;
  dateFilter?: 'future' | 'past' | 'all' | 'placeholder';
  signupStatusOptions?: SortableOption[];
  availabilityStatusOptions?: SortableOption[];
  onRefreshOffers?: () => Promise<void>;
  isAdmin?: boolean;
  /** When set, show a Location column to the left of Session title (e.g. org-level sessions tab). */
  columnOptions?: CreateColumnsOptions;
}

export const SessionsTable: React.FC<SessionsTableProps> = ({ 
  offers, 
  isLoading, 
  onEditOffer, 
  onDeleteOffer,
  onCopyOffer,
  onBulkUpdateOffers,
  onDateFilterChange,
  dateFilter = 'future',
  signupStatusOptions = [],
  availabilityStatusOptions = [],
  onRefreshOffers,
  isAdmin,
  columnOptions
}) => {
  const [sortedOffers, setSortedOffers] = useState<Offer[]>([]);
  
  // Add ref for PaginatedTable
  const paginatedTable = React.useRef<any>(null);
  
  // Use our custom hook for table state and handlers
  const {
    selectedOffers,
    setSelectedOffers,
    searchTerm,
    pageSize,
    isDeleteDialogOpen,
    setIsDeleteDialogOpen,
    offerToDelete,
    isBulkEditModalOpen,
    setIsBulkEditModalOpen,
    isBulkDeleteDialogOpen,
    setIsBulkDeleteDialogOpen,
    handleRowSelect,
    handleDeleteClick,
    handleConfirmDelete,
    handleBulkDelete,
    handleConfirmBulkDelete,
    handleBulkEdit,
    handleSaveBulkEdit,
    handleSearchChange,
    handlePageSizeChange
  } = useSessionsTable(
    offers, 
    onEditOffer, 
    onDeleteOffer, 
    onCopyOffer, 
    onBulkUpdateOffers,
    onRefreshOffers // Pass the refresh function to the hook
  );
  
  // Sort offers by start date on component mount and when offers change
  useEffect(() => {
    if (offers && offers.length > 0) {
      const sorted = [...offers].sort((a, b) => {
        // Use toDate helper to properly handle Timestamp objects
        const dateA = new Date(a.startDate instanceof Date ? a.startDate : a.startDate.toDate());
        const dateB = new Date(b.startDate instanceof Date ? b.startDate : b.startDate.toDate());
        return dateA.getTime() - dateB.getTime(); // Ascending order (earliest to latest)
      });
      setSortedOffers(sorted);
    } else {
      setSortedOffers([]);
    }
  }, [offers]);
  
  // Handle date filter change
  const handleDateFilterChange = (value: string) => {
    if (onDateFilterChange) {
      onDateFilterChange(value as 'future' | 'past' | 'all' | 'placeholder');
    }
  };
  
  // Handle page size change with table reference
  const handleTablePageSizeChange = (value: string) => {
    handlePageSizeChange(value);
    if (paginatedTable.current) {
      paginatedTable.current.handlePageSizeChange(value);
    }
  };
  
  // Render bulk actions button
  const renderBulkActions = (selectedItems: Offer[]) => (
    selectedItems.length > 0 ? (
      <BulkActionsMenu 
        selectedCount={selectedItems.length}
        onBulkDelete={handleBulkDelete}
        onBulkEdit={handleBulkEdit}
      />
    ) : null
  );
  
  // Custom header render with search input
  const renderCustomHeader = () => (
    <TableHeader
      searchTerm={searchTerm}
      onSearchChange={handleSearchChange}
      dateFilter={dateFilter}
      onDateFilterChange={handleDateFilterChange}
      pageSize={pageSize}
      onPageSizeChange={handleTablePageSizeChange}
      selectedOffers={selectedOffers}
      renderBulkActions={renderBulkActions}
      isAdmin={isAdmin}
    />
  );
  
  const columns = createColumns(
    onEditOffer,
    handleDeleteClick,
    onCopyOffer,
    columnOptions
  );
  
  // Create a row selection handler that integrates with the sorted offers
  const handleTableRowSelect = (offer: Offer, index: number, event: React.MouseEvent) => {
    // Extract checked state from event if it was passed from checkbox click
    const checked = (event as any).__checked;
    handleRowSelect(offer, index, event, sortedOffers, checked);
  };

  const handleBulkEditSave = (formData: BulkEditFormData) => {
    handleSaveBulkEdit(formData);
  };

  return (
    <>
      <PaginatedTable
        ref={paginatedTable}
        data={sortedOffers}
        columns={columns}
        keyExtractor={(offer: Offer) => offer.id}
        isLoading={isLoading}
        searchable={false}
        noDataText="No sessions found"
        renderHeader={renderCustomHeader}
        searchTerm={searchTerm}
        selectable={true}
        onSelectionChange={setSelectedOffers}
        selectedItems={selectedOffers}
        onRowSelect={handleTableRowSelect}
        pageSizeOptions={[25, 50, 100]}
        hidePageSizeSelector={true}
      />
      
      {/* Single Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        isOpen={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        onConfirm={handleConfirmDelete}
        isBulkDelete={false}
      />
      
      {/* Bulk Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        isOpen={isBulkDeleteDialogOpen}
        onOpenChange={setIsBulkDeleteDialogOpen}
        onConfirm={handleConfirmBulkDelete}
        isBulkDelete={true}
        selectedCount={selectedOffers.length}
      />
      
      {/* Bulk Edit Modal */}
      <BulkEditModal
        isOpen={isBulkEditModalOpen}
        onClose={() => setIsBulkEditModalOpen(false)}
        onSave={handleBulkEditSave}
        selectedOffers={selectedOffers}
        signupStatusOptions={signupStatusOptions}
        availabilityStatusOptions={availabilityStatusOptions}
      />
    </>
  );
};
